/* eslint-disable no-undef */
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Modal, Button, Form } from "react-bootstrap";
import axios from "axios";
import { getHeaders } from "../../utility/getHeader";
import fetchData from "../../functions/fetchData";
import CardComponent from "../../components/cards/CardComponent";
import { FaCashRegister, FaChartBar, FaSignOutAlt, FaStore, FaKey } from "react-icons/fa";

export default function CashierDashboard() {
  const navigate = useNavigate();
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [userData, setUserData] = useState([]);
  const [userId, setUserId] = useState(0);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");

  // Fetch user data on component mount
  useEffect(() => {
    const fetchAndSetData = async () => {
      try {
        await fetchData("users", setUserData, "id", {
          uname: localStorage.getItem("uname") || sessionStorage.getItem("uname"),
        });
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    };

    fetchAndSetData();
  }, []);

  // Extract user ID from fetched data
  useEffect(() => {
    if (userData && userData.length > 0) {
      const fetchedUserId = userData[0].id;
      setUserId(fetchedUserId);
    }
  }, [userData]);

  const handleLogout = () => {
    // Clear auth tokens
    localStorage.removeItem('token');
    sessionStorage.removeItem('token');
    localStorage.removeItem('uname');
    sessionStorage.removeItem('uname');
    
    toast.success("Logged out successfully!");
    
    // Navigate to login page
    setTimeout(() => {
      navigate('/login');
    }, 1000);
  };

  const handlePasswordUpdate = async (e) => {
    e.preventDefault();
    setError("");

    if (password !== confirmPassword) {
      setError("Passwords do not match!");
      toast.error("Passwords do not match!");
      return;
    }

    if (password.length < 6) {
      setError("Password must be at least 6 characters long!");
      toast.error("Password must be at least 6 characters long!");
      return;
    }

    try {
      const response = await axios.put(
        "/updatedata1/users/id/" + userId,
        { pass: password },
        getHeaders()
      );
      
      toast.success("Password Updated Successfully!");
      setShowPasswordModal(false);
      setPassword("");
      setConfirmPassword("");
      setError("");
    } catch (error) {
      console.error(error);
      toast.error("Error while updating password!");
    }
  };

  const handleCloseModal = () => {
    setShowPasswordModal(false);
    setPassword("");
    setConfirmPassword("");
    setError("");
  };

  const navigateToPage = (path) => {
    navigate(path);
  };

  // Responsive styles
  const containerStyle = {
    width: '100vw', 
    height: '100vh', 
    backgroundColor: '#f8f9fa',
    position: 'relative',
    overflow: 'auto',
    display: 'flex',
    flexDirection: 'column'
  };

  const headerStyle = {
    backgroundColor: '#bd510aff',
    color: 'white',
    padding: '20px',
    textAlign: 'center',
    boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
  };

  const mainContentStyle = {
    flex: 1,
    padding: '40px 20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  };

  const cardContentStyle = {
    padding: '30px 20px'
  };

  const buttonStyle = {
    width: '100%',
    padding: '12px 24px',
    fontSize: '16px',
    fontWeight: '600',
    borderRadius: '8px',
    border: 'none',
    transition: 'all 0.3s ease'
  };

  const footerStyle = {
    backgroundColor: '#6c757d',
    color: 'white',
    padding: '15px 20px',
    textAlign: 'center',
    fontSize: '14px'
  };

  // Responsive adjustments
  const isMobile = window.innerWidth <= 768;
  
  if (isMobile) {
    mainContentStyle.padding = '20px 15px';
    cardContentStyle.padding = '20px 15px';
    buttonStyle.fontSize = '14px';
    buttonStyle.padding = '10px 20px';
  }

  return (
    <>
      {/* ✅ Full Screen Cashier Dashboard Container */}
      <div style={containerStyle}>
        
        {/* ✅ Dashboard Header */}
        <div style={headerStyle}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', flexWrap: 'wrap' }}>
            <FaStore size={isMobile ? 20 : 24} />
            <h2 style={{ margin: 0, fontWeight: 'bold', fontSize: isMobile ? '1.5rem' : '2rem' }}> Cashier Dashboard</h2>
          </div>
          <p style={{ margin: '5px 0 0 0', opacity: 0.8, fontSize: isMobile ? '14px' : '16px' }}>Welcome back! Select an option below to continue</p>
        </div>

        <ToastContainer />
        
        {/* ✅ Main Content Area */}
        <div style={mainContentStyle}>
          
          {/* ✅ Navigation Cards Container */}
          <div className="container">
            <div className="row justify-content-center">
              
              {/* POS Page Card */}
              <div className="col-lg-4 col-md-6 col-sm-12 mb-4">
                <CardComponent
                  title="Point of Sale (POS)"
                  headerColor="primary"
                  pull="center"
                  bodyClass="text-center"
                  style={{ height: '100%' }}
                >
                  <div style={cardContentStyle}>
                    <FaCashRegister 
                      size={isMobile ? 50 : 60} 
                      style={{ 
                        color: '#007bff', 
                        marginBottom: '20px',
                        display: 'block',
                        margin: '0 auto 20px auto'
                      }} 
                    />
                    <h5 style={{ fontWeight: 'bold', marginBottom: '15px', color: '#333', fontSize: isMobile ? '1.1rem' : '1.25rem' }}>
                      POS System
                    </h5>
                    <p style={{ color: '#666', marginBottom: '25px', fontSize: isMobile ? '13px' : '14px' }}>
                      Process sales, manage orders, and handle customer transactions
                    </p>
                    <button 
                      className="btn btn-primary btn-lg"
                      onClick={() => navigateToPage('/pos/ticketbilling')}
                      style={{ 
                        ...buttonStyle,
                        background: 'linear-gradient(45deg, #007bff, #0056b3)',
                        boxShadow: '0 4px 12px rgba(0,123,255,0.3)'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = "translateY(-2px)";
                        e.currentTarget.style.boxShadow = "0 6px 16px rgba(0,123,255,0.4)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "translateY(0)";
                        e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,123,255,0.3)";
                      }}
                    >
                      Open POS
                    </button>
                  </div>
                </CardComponent>
              </div>

              {/* Sale Reports Card */}
              <div className="col-lg-4 col-md-6 col-sm-12 mb-4">
                <CardComponent
                  title="Summary Reports"
                  headerColor="success"
                  pull="center"
                  bodyClass="text-center"
                  style={{ height: '100%' }}
                >
                  <div style={cardContentStyle}>
                    <FaChartBar 
                      size={isMobile ? 50 : 60} 
                      style={{ 
                        color: '#28a745', 
                        marginBottom: '20px',
                        display: 'block',
                        margin: '0 auto 20px auto'
                      }} 
                    />
                    <h5 style={{ fontWeight: 'bold', marginBottom: '15px', color: '#333', fontSize: isMobile ? '1.1rem' : '1.25rem' }}>
                      Cashier Summary Counter
                    </h5>
                    <p style={{ color: '#666', marginBottom: '25px', fontSize: isMobile ? '13px' : '14px' }}>
                      View sales analytics, daily reports, and transaction history
                    </p>
                    <button 
                      className="btn btn-success btn-lg"
                      onClick={() => navigateToPage('/pos/cashier-summary')}
                      style={{ 
                        ...buttonStyle,
                        background: 'linear-gradient(45deg, #28a745, #1e7e34)',
                        boxShadow: '0 4px 12px rgba(40,167,69,0.3)'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = "translateY(-2px)";
                        e.currentTarget.style.boxShadow = "0 6px 16px rgba(40,167,69,0.4)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "translateY(0)";
                        e.currentTarget.style.boxShadow = "0 4px 12px rgba(40,167,69,0.3)";
                      }}
                    >
                      View Reports
                    </button>
                  </div>
                </CardComponent>
              </div>

              {/* Logout Card */}
              <div className="col-lg-4 col-md-6 col-sm-12 mb-4">
                <CardComponent
                  title="Account"
                  headerColor="danger"
                  pull="center"
                  bodyClass="text-center"
                  style={{ height: '100%' }}
                >
                  <div style={cardContentStyle}>
                    <FaSignOutAlt 
                      size={isMobile ? 50 : 60} 
                      style={{ 
                        color: '#dc3545', 
                        marginBottom: '20px',
                        display: 'block',
                        margin: '0 auto 20px auto'
                      }} 
                    />
                    <h5 style={{ fontWeight: 'bold', marginBottom: '15px', color: '#333', fontSize: isMobile ? '1.1rem' : '1.25rem' }}>
                      Account Settings
                    </h5>
                    <p style={{ color: '#666', marginBottom: '25px', fontSize: isMobile ? '13px' : '14px' }}>
                      Change your password or end your session
                    </p>
                    
                    {/* Change Password Button */}
                    <button 
                      className="btn btn-warning btn-lg mb-3"
                      onClick={() => setShowPasswordModal(true)}
                      style={{ 
                        ...buttonStyle,
                        background: 'linear-gradient(45deg, #ffc107, #ff9800)',
                        boxShadow: '0 4px 12px rgba(255,193,7,0.3)',
                        marginBottom: '15px'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = "translateY(-2px)";
                        e.currentTarget.style.boxShadow = "0 6px 16px rgba(255,193,7,0.4)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "translateY(0)";
                        e.currentTarget.style.boxShadow = "0 4px 12px rgba(255,193,7,0.3)";
                      }}
                    >
                      <FaKey style={{ marginRight: '8px' }} />
                      Change Password
                    </button>
                    
                    {/* Logout Button */}
                    <button 
                      className="btn btn-danger btn-lg"
                      onClick={handleLogout}
                      style={{ 
                        ...buttonStyle,
                        background: 'linear-gradient(45deg, #dc3545, #c82333)',
                        boxShadow: '0 4px 12px rgba(220,53,69,0.3)'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = "translateY(-2px)";
                        e.currentTarget.style.boxShadow = "0 6px 16px rgba(220,53,69,0.4)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "translateY(0)";
                        e.currentTarget.style.boxShadow = "0 4px 12px rgba(220,53,69,0.3)";
                      }}
                    >
                      Logout
                    </button>
                  </div>
                </CardComponent>
              </div>

            </div>
          </div>
          
        </div> {/* ✅ Close main content area */}
        
        {/* ✅ Footer */}
        <div style={footerStyle}>
          <p style={{ margin: 0 }}>
            © 2025 ChefMate POS System | Cashier Station | 
            <span style={{ marginLeft: '10px', opacity: 0.8 }}>
              {new Date().toLocaleDateString()}
            </span>
          </p>
        </div>
        
      </div> {/* ✅ Close full screen container */}

      {/* Change Password Modal */}
      <Modal show={showPasswordModal} onHide={handleCloseModal} centered>
        <Modal.Header closeButton style={{ backgroundColor: '#ffc107', color: '#333' }}>
          <Modal.Title>
            <FaKey style={{ marginRight: '10px' }} />
            Change Password
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handlePasswordUpdate}>
            <Form.Group className="mb-3">
              <Form.Label>New Password *</Form.Label>
              <Form.Control
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your new password"
                required
                minLength={6}
              />
              <Form.Text className="text-muted">
                Password must be at least 6 characters long
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Confirm New Password *</Form.Label>
              <Form.Control
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm your new password"
                required
              />
            </Form.Group>

            {error && <p style={{ color: "red", marginBottom: "15px" }}>{error}</p>}

            <div className="d-flex gap-2 justify-content-end">
              <Button variant="secondary" onClick={handleCloseModal}>
                Cancel
              </Button>
              <Button variant="warning" type="submit">
                Change Password
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </>
  );
}
